import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Mail, 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  UserPlus, 
  LogIn,
  Shield,
  Crown,
  Star,
  CheckCircle,
  MessageCircle,
  Infinity
} from 'lucide-react';

const ModernLoginPage = ({ onLogin, onSwitchToRegister, loginWithEmail }) => {
  const [loginType, setLoginType] = useState('email'); // email, google, guest
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Responsive detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (loginWithEmail) {
        await loginWithEmail(formData.email, formData.password);
        toast.success('Başarıyla giriş yapıldı!');
        onLogin();
      } else {
        toast.error('Email girişi henüz aktif değil!');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Giriş yapılırken bir hata oluştu!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Google OAuth için yeni sekme aç
    window.open('/google-callback.html', '_blank');
  };

  const handleGuestLogin = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post('/api/auth/guest');
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success('Misafir olarak giriş yapıldı!');
        onLogin();
      }
    } catch (error) {
      console.error('Guest login error:', error);
      toast.error('Misafir girişi sırasında hata oluştu!');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`h-screen gokturk-bg flex flex-col relative overflow-hidden ${
      isMobile ? 'p-2' : isTablet ? 'p-4' : 'p-6'
    }`}>
      {/* Göktürk Arka Plan Deseni */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-blue-800/20 to-blue-700/20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8ZGVmcz4KICAgIDxmaWx0ZXIgaWQ9IndvbGYtc3ltYm9sIj4KICAgICAgPGZlR2F1c3NpYW5CbHVyIHN0ZERldmlhdGlvbj0iMyIvPgogICAgPC9maWx0ZXI+CiAgPC9kZWZzPgogIDxwYXRoIGQ9Ik0xMDAgMjBjLTQwIDAtNzAgMzAtNzAgNzBzMzAgNzAgNzAgNzAgNzAtMzAgNzAtNzAtMzAtNzAtNzAtNzB6IiBmaWxsPSIjMWUzYTg4YSIgZmlsdGVyPSJ1cmwoI3dvbGYtc3ltYm9sKSIgb3BhY2l0eT0iMC4xIi8+CiAgPHRleHQgeD0iMTAwIiB5PSIxMDAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSI0MCIgZmlsbD0iIzFjNDBhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPvCfkY08L3RleHQ+Cjwvc3ZnPg==')] opacity-10"></div>
        
        {/* Göktürk Sembolleri */}
        <div className="absolute top-20 left-20 text-blue-300/30 text-6xl">🐺</div>
        <div className="absolute top-32 right-32 text-blue-400/30 text-4xl">⚔️</div>
        <div className="absolute bottom-32 left-32 text-blue-500/30 text-5xl">🛡️</div>
        <div className="absolute bottom-20 right-20 text-yellow-400/30 text-3xl">👑</div>
      </div>

      {/* Ana Başlık - Ortada */}
      <motion.div 
        className="relative z-10 py-4"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        <motion.h1 
          className="text-2xl md:text-4xl lg:text-5xl font-bold text-center gokturk-main-title"
          animate={{ 
            textShadow: [
              "0 0 20px rgba(59, 130, 246, 0.8), 0 0 40px rgba(59, 130, 246, 0.6), 0 0 60px rgba(59, 130, 246, 0.4)",
              "0 0 30px rgba(59, 130, 246, 1), 0 0 60px rgba(59, 130, 246, 0.8), 0 0 90px rgba(59, 130, 246, 0.6)",
              "0 0 20px rgba(59, 130, 246, 0.8), 0 0 40px rgba(59, 130, 246, 0.6), 0 0 60px rgba(59, 130, 246, 0.4)"
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          role="banner"
          aria-label="GöktürkGPT Ana Başlık"
        >
          🐺 GÖKTÜRKGPT - TÜRK YAPAY ZEKASI 🐺
        </motion.h1>
        <motion.p 
          className="text-base md:text-lg mt-3 text-center font-medium gokturk-subtitle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          role="banner"
          aria-label="GöktürkGPT Açıklama"
        >
          Türk Tarihinin Derinliklerinden Modern Teknolojiyle Buluşan Yapay Zeka
        </motion.p>
      </motion.div>

      {/* Özel İndirim Banner */}
      <motion.div 
        className="relative z-30 py-2 flex justify-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
      >
        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white px-8 py-4 rounded-full shadow-2xl border-2 border-yellow-400 backdrop-blur-sm max-w-md mx-auto">
          <div className="flex items-center justify-center space-x-3">
            <span className="text-2xl">🐺</span>
            <div className="text-center">
              <div className="font-bold text-lg">ÖZEL FIRSAT!</div>
              <div className="text-yellow-200 text-sm">Tüm Premium Planlarda %50 İndirim</div>
            </div>
            <span className="text-2xl">🐺</span>
          </div>
        </div>
      </motion.div>

      {/* Ana İçerik - Scrollable */}
      <div className="flex-1 overflow-y-auto hide-scrollbar">
        <div className={`relative z-20 w-full max-w-7xl mx-auto grid gap-6 ${
          isMobile 
            ? 'grid-cols-1' 
            : isTablet 
              ? 'grid-cols-1 md:grid-cols-2' 
              : 'grid-cols-1 lg:grid-cols-3'
        }`}>
        {/* Göktürk Login Form */}
        <motion.div 
          className={`gokturk-card ${
            isMobile ? 'p-6' : isTablet ? 'p-8' : 'p-10'
          }`}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className={`text-center ${isMobile ? 'mb-4' : 'mb-6'}`}>
            <h2 className={`font-bold gokturk-title mb-2 ${
              isMobile ? 'text-lg' : isTablet ? 'text-xl' : 'text-2xl'
            }`}>🐺 GöktürkGPT'e Hoş Geldiniz</h2>
            <p className={`gokturk-text ${isMobile ? 'text-xs' : 'text-sm'}`}>Türk Yapay Zekası ile Sohbet Edin</p>
            <div className={`mt-2 text-blue-200 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              <p>🐺 Türk Tarihinin Derinliklerinden</p>
              <p>🤖 Modern Teknolojiyle Buluşan</p>
            </div>
          </div>

          {/* Login Type Tabs */}
          <div className="flex mb-6 bg-white/10 rounded-lg p-1">
            {[
              { id: 'email', label: 'Email', icon: Mail },
              { id: 'google', label: 'Google', icon: Mail },
              { id: 'guest', label: 'Misafir', icon: User }
            ].map((type) => (
              <button
                key={type.id}
                onClick={() => setLoginType(type.id)}
                className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-md transition-all duration-200 ${
                  loginType === type.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-blue-200 hover:bg-white/10'
                }`}
              >
                <type.icon className="w-4 h-4" />
                <span className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  {type.label}
                </span>
              </button>
            ))}
          </div>

          {/* Email Login Form */}
          {loginType === 'email' && (
            <motion.form
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              onSubmit={handleEmailLogin}
              className="space-y-4"
            >
              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">
                  Email Adresi
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-300 w-5 h-5" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-blue-400/30 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ornek@email.com"
                    required
                    aria-label="E-posta adresi"
                    aria-required="true"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">
                  Şifre
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-300 w-5 h-5" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-12 py-3 bg-white/10 border border-blue-400/30 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="••••••••"
                    required
                    aria-label="Şifre"
                    aria-required="true"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300 hover:text-white transition-colors"
                    aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full gokturk-btn py-3 px-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Giriş Yapılıyor...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>Giriş Yap</span>
                  </>
                )}
              </button>
            </motion.form>
          )}

          {/* Google Login */}
          {loginType === 'google' && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <button
                onClick={handleGoogleLogin}
                className="w-full bg-white hover:bg-gray-100 text-gray-800 py-3 px-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center space-x-2 border-2 border-gray-300"
              >
                <Mail className="w-5 h-5" />
                <span>Google ile Giriş Yap</span>
              </button>
            </motion.div>
          )}

          {/* Guest Login */}
          {loginType === 'guest' && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <button
                onClick={handleGuestLogin}
                disabled={isLoading}
                className="w-full gokturk-btn py-3 px-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Misafir Girişi...</span>
                  </>
                ) : (
                  <>
                    <User className="w-5 h-5" />
                    <span>Misafir Olarak Devam Et</span>
                  </>
                )}
              </button>
            </motion.div>
          )}

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-blue-200 text-sm">
              Hesabınız yok mu?{' '}
              <button
                onClick={onSwitchToRegister}
                className="text-yellow-400 hover:text-yellow-300 font-semibold transition-colors duration-200 flex items-center justify-center space-x-1 mx-auto"
              >
                <UserPlus className="w-4 h-4" />
                <span>Kayıt Ol</span>
              </button>
            </p>
          </div>

          {/* Security Info */}
          <div className="mt-6 text-center">
            <p className="text-white/60 text-sm">
              <Shield className="w-4 h-4 inline mr-1" />
              Güvenli ve şifrelenmiş bağlantı
            </p>
          </div>
        </motion.div>

        {/* Premium Özellikler */}
        <motion.div 
          className={`gokturk-card ${
            isMobile ? 'p-6' : isTablet ? 'p-8' : 'p-10'
          }`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className={`text-center ${isMobile ? 'mb-4' : 'mb-6'}`}>
            <h3 className={`font-bold gokturk-title mb-2 ${
              isMobile ? 'text-lg' : isTablet ? 'text-xl' : 'text-2xl'
            }`}>👑 Premium Özellikler</h3>
            <p className={`gokturk-text ${isMobile ? 'text-sm' : 'text-base'}`}>GöktürkGPT'in Tüm Gücünü Keşfedin</p>
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto hide-scrollbar">
            {/* Premium Plan Cards */}
            {[
              {
                name: 'Premium Basic',
                price: '₺99',
                originalPrice: '₺199',
                discount: '%50',
                features: ['Sınırsız Sohbet', 'Görsel Üretimi', 'Video Üretimi', 'Özel Temalar'],
                icon: Star,
                color: 'from-blue-500 to-blue-600'
              },
              {
                name: 'Premium Pro',
                price: '₺199',
                originalPrice: '₺399',
                discount: '%50',
                features: ['Tüm Basic Özellikler', 'API Erişimi', 'Öncelikli Destek', 'Gelişmiş AI Modelleri'],
                icon: Crown,
                color: 'from-purple-500 to-purple-600'
              },
              {
                name: 'Premium Enterprise',
                price: '₺499',
                originalPrice: '₺999',
                discount: '%50',
                features: ['Tüm Pro Özellikler', 'Özel Entegrasyon', '7/24 Destek', 'Özel AI Eğitimi'],
                icon: Infinity,
                color: 'from-yellow-500 to-orange-500'
              }
            ].map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                className={`bg-gradient-to-r ${plan.color} rounded-lg p-4 border border-white/20 hover:border-white/40 transition-all duration-300 cursor-pointer group`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <plan.icon className="w-6 h-6 text-white" />
                    <h4 className="text-white font-bold text-lg">{plan.name}</h4>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-bold text-xl">{plan.price}</div>
                    <div className="text-white/70 text-sm line-through">{plan.originalPrice}</div>
                    <div className="text-yellow-300 text-xs font-bold">{plan.discount} İndirim!</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-300" />
                      <span className="text-white/90 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                <button className="w-full mt-4 bg-white/20 hover:bg-white/30 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 group-hover:bg-white/40">
                  Seç
                </button>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Göktürk Sohbet Önizleme */}
        <motion.div 
          className={`gokturk-card ${
            isMobile ? 'p-6' : isTablet ? 'p-8' : 'p-10'
          }`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className={`text-center ${isMobile ? 'mb-4' : 'mb-6'}`}>
            <h3 className={`font-bold gokturk-title mb-2 ${
              isMobile ? 'text-lg' : isTablet ? 'text-xl' : 'text-2xl'
            }`}>🐺 Sohbet Önizleme</h3>
            <p className={`gokturk-text ${isMobile ? 'text-sm' : 'text-base'}`}>GöktürkGPT ile nasıl sohbet edeceksiniz</p>
          </div>

          <div className={`space-y-3 max-h-80 overflow-y-auto hide-scrollbar`}>
            {/* Örnek Sohbet Mesajları */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
              className="bg-blue-500/20 rounded-lg p-3 border border-blue-400/30"
            >
              <div className="flex items-start space-x-2">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                  S
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Sen</p>
                  <p className="text-blue-100 text-sm">Türk tarihindeki en önemli olaylar nelerdir?</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.6 }}
              className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-3 border border-blue-400/30"
            >
              <div className="flex items-start space-x-2">
                <div className="w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                  🐺
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">GöktürkGPT</p>
                  <p className="text-blue-100 text-sm">Türk tarihindeki en önemli olaylar arasında Göktürk Kağanlığı'nın kuruluşu, Malazgirt Savaşı, İstanbul'un fethi ve Kurtuluş Savaşı yer alır. Bu olaylar Türk milletinin tarihini şekillendiren kritik dönüm noktalarıdır.</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.7 }}
              className="bg-blue-500/20 rounded-lg p-3 border border-blue-400/30"
            >
              <div className="flex items-start space-x-2">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                  S
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Sen</p>
                  <p className="text-blue-100 text-sm">Yapay zeka nasıl çalışır?</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.8 }}
              className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-3 border border-blue-400/30"
            >
              <div className="flex items-start space-x-2">
                <div className="w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                  🐺
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">GöktürkGPT</p>
                  <p className="text-blue-100 text-sm">Yapay zeka, büyük veri setleri üzerinde eğitilen algoritmalar sayesinde insan benzeri düşünme ve karar verme yetenekleri geliştirir. Makine öğrenmesi ve derin öğrenme teknikleri kullanarak sürekli gelişir.</p>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="mt-4 text-center">
            <p className="text-white/60 text-sm">
              <MessageCircle className="w-4 h-4 inline mr-1" />
              Gerçek zamanlı sohbet deneyimi
            </p>
          </div>
        </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ModernLoginPage;